import React, { useEffect, useState } from 'react';
import { useFlashFonic } from '../contexts/FlashFonicContext.js';
import { formatAutoFlashInterval, sliderToInterval, intervalToSlider } from '../utils/time.js';

/**
 * Live audio capture controls — shared by FlashFonic and FlashClinical (AppHome).
 * Must be rendered inside FlashFonicProvider.
 */
const FonicLiveCapture = ({
    targetFolderId = '',
    onTargetFolderChange,
    folders = [],
    onCreateFolder,
    isMovingQueueCards = false,
    showFolderTargets = true,
}) => {
    const ff = useFlashFonic();
    const [listenSlider, setListenSlider] = useState(ff?.listeningDuration ?? 0);
    // Collapsed by default on mobile, always open on desktop — mirrors the "Settings"
    // accordion pattern in FlashClinicalPanel.jsx exactly (same toggle button styling,
    // same lg:hidden/lg:block behavior).
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        setListenSlider(ff?.listeningDuration ?? 0);
    }, [ff?.listeningDuration]);

    return (
        <>
            <p className="text-slate-300 mb-4">
                Choose a listening duration using the slider below. Tap the button to start. Hit &quot;Flash It!&quot; to create a card, or use voice/auto features.
            </p>
            <div className="grid gap-4">
                <button
                    type="button"
                    onClick={() => (ff?.isListening ? ff?.stopListening() : ff?.startListening())}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-medium shadow-lg ${
                        ff?.isListening
                            ? 'text-slate-200 bg-slate-800/70 ring-1 ring-white/10'
                            : 'text-white bg-gradient-to-r from-teal-600 to-emerald-600 shadow-teal-500/20'
                    }`}
                >
                    {ff?.isListening ? '■ Stop Listening' : '● Start Listening'}
                </button>
                {/* The primary action — bigger and visually distinct (brand gradient) from
                    the two secondary toggles below it, and reachable without scrolling once
                    you're mid-recording. */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        ff?.handleLiveFlashIt && ff.handleLiveFlashIt();
                    }}
                    className="flash-it-button w-full inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-5 text-lg font-semibold ring-1 ring-white/10"
                >
                    ⚡ Flash It!
                </button>
                <div className="grid grid-cols-2 gap-2.5">
                    <button
                        type="button"
                        onClick={() => ff?.setVoiceActivated && ff.setVoiceActivated((v) => !v)}
                        className={`w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium ring-1 transition ${
                            ff?.voiceActivated ? 'text-teal-200 bg-teal-500/15 ring-teal-400/30' : 'text-slate-300 bg-slate-800/60 ring-white/10'
                        }`}
                    >
                        Voice Activate
                    </button>
                    <button
                        type="button"
                        onClick={() => ff?.setIsAutoFlashOn && ff.setIsAutoFlashOn((v) => !v)}
                        className={`w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium ring-1 transition ${
                            ff?.isAutoFlashOn ? 'text-emerald-200 bg-emerald-500/15 ring-emerald-400/30' : 'text-slate-300 bg-slate-800/60 ring-white/10'
                        }`}
                    >
                        Auto-Flash
                    </button>
                </div>
                {ff?.voiceActivated && (
                    <div className="mt-2 text-center text-slate-300">🎤 Just say &quot;Flash it&quot; to create a flashcard!</div>
                )}

                <div>
                    <button
                        type="button"
                        onClick={() => setSettingsOpen((o) => !o)}
                        className="lg:hidden w-full flex items-center justify-between rounded-xl bg-white/4 border border-white/8 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/6 transition mb-3"
                    >
                        <span className="font-medium">Settings</span>
                        <svg
                            className={`h-4 w-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    <div className={`${settingsOpen ? 'block' : 'hidden'} lg:block space-y-4`}>
                <div className="text-center text-slate-300">
                    Listening Duration:{' '}
                    <span id="ff-duration-label" className="font-medium text-teal-300">
                        {listenSlider === 0 ? 'continuous' : `${listenSlider} minute${listenSlider > 1 ? 's' : ''}`}
                    </span>
                </div>
                <div className="relative">
                    <div className="h-2 w-full rounded-full bg-slate-800/80" />
                    <div
                        id="ff-duration-fill"
                        className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600"
                        style={{ width: `${((listenSlider ?? 0) / 10) * 100}%`, pointerEvents: 'none' }}
                    />
                    <input
                        id="ff-duration"
                        type="range"
                        min="0"
                        max="10"
                        step="1"
                        value={listenSlider}
                        onChange={(e) => {
                            const v = Number(e.target.value);
                            setListenSlider(v);
                            if (ff?.setListeningDuration) ff.setListeningDuration(v);
                        }}
                        className="absolute inset-0 w-full h-8 cursor-pointer z-20"
                        style={{ opacity: 0.001, WebkitAppearance: 'none', appearance: 'none' }}
                    />
                    <div
                        id="ff-duration-thumb"
                        className="absolute -top-[6px] h-5 w-5 rounded-full bg-white shadow-lg shadow-teal-500/20 ring-4 ring-teal-500/30"
                        style={{ left: `calc(${((listenSlider ?? 0) / 10) * 100}% - 10px)`, pointerEvents: 'none' }}
                    />
                </div>
                {ff?.isAutoFlashOn ? (
                    <>
                        <div className="text-center text-slate-300">
                            Auto-Flash Interval:{' '}
                            <span className="font-medium text-teal-300">
                                {formatAutoFlashInterval(ff?.autoFlashInterval || 20)}
                            </span>
                        </div>
                        <div className="relative">
                            <div className="h-2 w-full rounded-full bg-slate-800/80" />
                            <div
                                className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600"
                                style={{ width: `${((intervalToSlider(ff?.autoFlashInterval || 20)) / 8) * 100}%`, pointerEvents: 'none' }}
                            />
                            <input
                                type="range"
                                min="0"
                                max="8"
                                step="1"
                                value={intervalToSlider(ff?.autoFlashInterval || 20)}
                                onChange={(e) =>
                                    ff?.setAutoFlashInterval && ff.setAutoFlashInterval(sliderToInterval(Number(e.target.value)))
                                }
                                className="absolute inset-0 w-full h-8 cursor-pointer z-20"
                                style={{ opacity: 0.001, WebkitAppearance: 'none', appearance: 'none' }}
                            />
                            <div
                                className="absolute -top-[6px] h-5 w-5 rounded-full bg-white shadow-lg shadow-teal-500/20 ring-4 ring-teal-500/30"
                                style={{
                                    left: `calc(${((intervalToSlider(ff?.autoFlashInterval || 20)) / 8) * 100}% - 10px)`,
                                    pointerEvents: 'none',
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-center text-slate-300">
                            Capture Last:{' '}
                            <span id="ff-capture-label" className="font-medium text-teal-300">
                                {ff?.captureLastSeconds || 15} seconds of audio
                            </span>
                        </div>
                        <div className="relative">
                            <div className="h-2 w-full rounded-full bg-slate-800/80" />
                            <div
                                id="ff-capture-fill"
                                className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600"
                                style={{ width: `${(((ff?.captureLastSeconds || 15) - 5) / (60 - 5)) * 100}%`, pointerEvents: 'none' }}
                            />
                            <input
                                id="ff-capture"
                                type="range"
                                min="5"
                                max="60"
                                step="5"
                                value={ff?.captureLastSeconds || 15}
                                onInput={(e) =>
                                    ff?.setCaptureLastSeconds && ff.setCaptureLastSeconds(Number(e.target.value))
                                }
                                className="absolute inset-0 w-full h-8 cursor-pointer z-20"
                                style={{ opacity: 0.001, WebkitAppearance: 'none', appearance: 'none' }}
                            />
                            <div
                                id="ff-capture-thumb"
                                className="absolute -top-[6px] h-5 w-5 rounded-full bg-white shadow-lg shadow-teal-500/20 ring-4 ring-teal-500/30"
                                style={{
                                    left: `calc(${(((ff?.captureLastSeconds || 15) - 5) / (60 - 5)) * 100}% - 10px)`,
                                    pointerEvents: 'none',
                                }}
                            />
                        </div>
                    </>
                )}
                    </div>
                </div>
                {showFolderTargets && onTargetFolderChange && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <select
                            className="rounded-xl bg-slate-800/70 border border-white/10 px-3.5 py-2.5 text-sm"
                            value={targetFolderId}
                            onChange={(e) => onTargetFolderChange(e.target.value)}
                            disabled={isMovingQueueCards}
                        >
                            <option value="">Select folder...</option>
                            {folders.map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.name}
                                </option>
                            ))}
                        </select>
                        {onCreateFolder && (
                            <button
                                type="button"
                                onClick={onCreateFolder}
                                className="inline-flex items-center justify-center rounded-xl px-3.5 py-2.5 text-sm font-medium bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-500 hover:to-emerald-500 shadow-lg shadow-teal-500/20"
                            >
                                Create New Folder
                            </button>
                        )}
                        <div className="text-xs text-slate-400 sm:col-span-2">
                            Tip: Generate cards, then select and move them from the Review Queue into a folder.
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default FonicLiveCapture;
